<template>
  <Teleport v-if="isTeleportReady" to="#teleported">
    <div
      v-if="isContainerShown"
      class="fixed inset-0 z-1000 grid h-screen w-screen place-items-center"
      @mousedown="handleOutsideClick"
      :id="backdropId"
    >
      <transition name="fade">
        <div
          v-if="isBackdropShown"
          class="absolute z-1001 h-screen w-full bg-black/75"
          :class="{ 'backdrop-blur-sm': isBackgroundBlurred }"
        />
      </transition>
      <transition
        enter-active-class="transition ease-out"
        enter-from-class="scale-95 opacity-0 -translate-y-4"
        enter-to-class="scale-100 opacity-100 translate-y-0"
        leave-active-class="ease-out"
        leave-from-class="scale-100 opacity-100 translate-y-0"
        leave-to-class="scale-95 opacity-0 -translate-y-4"
      >
        <div
          v-if="isModalShown"
          :class="
            twMerge(
              'z-1002 grid h-full w-screen place-items-center overflow-y-auto px-4 pb-32',
              modalClass,
            )
          "
        >
          <div
            role="dialog"
            aria-modal="true"
            :aria-labelledby="labelledby"
            :id="id"
            :class="twMerge('mx-auto w-full rounded-2xl bg-white', widthClass)"
          >
            <FocusLoop :is-active="isFocusLoopActive" :auto-focus="autoFocus">
              <slot />
            </FocusLoop>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script setup>
import FocusLoop from './FocusLoop.vue'
import { nextTick, onBeforeUnmount, onMounted, ref, watch, useId } from 'vue'
import { useModalsStore } from '@/shared/stores/modals.js'
import { twMerge } from 'tailwind-merge'

const emit = defineEmits(['show', 'hide'])

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  widthClass: {
    type: String,
    default: '',
  },
  modalClass: {
    type: String,
    default: 'pt-4',
  },
  clickOutsideToClose: {
    type: Boolean,
    default: true,
  },
  // Escape follows clickOutsideToClose unless set on its own. A modal can
  // refuse backdrop clicks and still close on Escape.
  escapeToClose: {
    type: Boolean,
    default: null,
  },
  isBackgroundBlurred: {
    type: Boolean,
    default: false,
  },
  autoFocus: {
    type: Boolean,
    default: true,
  },
  // The id of the modal's title, so a screen reader names the dialog.
  labelledby: {
    type: String,
    default: null,
  },
})

const { activeModals, closeModal } = useModalsStore()

const id = `growthmentor-modal-${useId()}`
const backdropId = `growthmentor-modal-backdrop-${useId()}`
const isVisible = ref(false)
const isTeleportReady = ref(false)
const isContainerShown = ref(false)
const isBackdropShown = ref(false)
const isModalShown = ref(false)
const isFocusLoopActive = ref(false)

onMounted(() => {
  isTeleportReady.value = true
})

watch(activeModals, (newActiveModals) => {
  isVisible.value = newActiveModals.includes(props.name)
})

watch(isVisible, (newValue) => {
  if (newValue) {
    lockBody()
    showModal()
    emit('show')
  } else {
    unlockBody()
    hideModal()
    emit('hide')
  }
})

const lockBody = () => {
  const scrollBarWidth =
    window.innerWidth - document.documentElement.clientWidth
  document.body.style.paddingRight = `${scrollBarWidth}px`
  document.body.style.overflow = 'hidden'
}

const unlockBody = () => {
  document.body.style.paddingRight = '0px'
  document.body.style.overflow = 'auto'
}

const animateModal = () => {
  document
    .getElementById(id)
    .animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.02)' },
        { transform: 'scale(1)' },
      ],
      {
        duration: 200,
      },
    )
}

const handleOutsideClick = (event) => {
  if (!isVisible.value) return

  const isClickedElementInsideModal = document
    .getElementById(id)
    .contains(event.target)

  if (isClickedElementInsideModal) return

  handleCloseModal(props.clickOutsideToClose)
}

const handleClickEscapeKey = (event) => {
  if (!isVisible.value || event.keyCode !== 27) return

  handleCloseModal(props.escapeToClose ?? props.clickOutsideToClose)
}

const handleCloseModal = (isAllowed) => {
  if (!isAllowed) {
    animateModal()
    return
  }

  closeModal(props.name)
}

const showModal = () => {
  isContainerShown.value = true

  nextTick(() => {
    isBackdropShown.value = true
    isModalShown.value = true
    isFocusLoopActive.value = true

    document.addEventListener('keyup', handleClickEscapeKey)
  })
}

const hideModal = () => {
  isFocusLoopActive.value = false

  nextTick(() => {
    isModalShown.value = false
    isBackdropShown.value = false
    isContainerShown.value = false

    document.removeEventListener('keyup', handleClickEscapeKey)
  })
}

// Make sure that the modal does not stay in the DOM after the component is destroyed
onBeforeUnmount(() => {
  unlockBody()

  document.removeEventListener('keyup', handleClickEscapeKey)

  const modalElement = document.getElementById(id)
  if (modalElement) modalElement.remove()

  const modalBackdropElement = document.getElementById(backdropId)
  if (modalBackdropElement) modalBackdropElement.remove()
})
</script>
